if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.viewerButtons = this.elements.viewer
          ? this.elements.viewer.querySelectorAll('button[name="previous"], button[name="next"]')
          : [];
        this.thumbnailButtons = this.elements.thumbnails
          ? this.elements.thumbnails.querySelectorAll('button[name="previous"], button[name="next"]')
          : [];
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.onThumbnailMediaClick.bind(this, mediaToSwitch.dataset.target));
        });
        this.viewerButtons.forEach((button) => {
          button.dataset.mediaGalleryNav = 'desktop';
          button.addEventListener('click', this.onViewerButtonClick.bind(this));
        });
        this.thumbnailButtons.forEach((button) => {
          button.dataset.mediaGalleryNav = 'thumbnail-strip';
          button.addEventListener('click', this.onThumbnailButtonClick.bind(this));
        });
        this.elements.thumbnails.slider?.addEventListener('scroll', debounce(this.updateThumbnailButtons.bind(this), 80));
        this.ensureActiveMedia();
        this.updateThumbnailButtons();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const isDesktopThumbnailGallery = this.shouldHandleDesktopMediaNav();
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        const activeImage = activeMedia.querySelector('.product__modal-opener--image img');
        if (activeImage) {
          activeImage.loading = 'eager';
          activeImage.fetchPriority = 'high';
          activeImage.removeAttribute('loading');
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!isDesktopThumbnailGallery && (!this.mql.matches || this.elements.thumbnails)) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.updateThumbnailButtons();
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      ensureActiveMedia() {
        const mediaItems = Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []);
        if (!mediaItems.length) return;
        if (mediaItems.some((item) => item.classList.contains('is-active'))) return;

        mediaItems[0].classList.add('is-active');
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      onViewerButtonClick(event) {
        if (!this.shouldHandleDesktopMediaNav()) return;
        event.preventDefault();
        this.navigateMedia(event.currentTarget.name === 'next' ? 1 : -1);
      }

      onThumbnailMediaClick(mediaId, event) {
        event.preventDefault();
        this.setActiveMedia(mediaId, false);
      }

      onThumbnailButtonClick(event) {
        if (!this.shouldHandleDesktopMediaNav()) return;
        event.preventDefault();
        const slider = this.elements.thumbnails?.slider;
        if (!slider) return;

        const scrollAmount = Math.max(slider.clientWidth * 0.72, 1);
        slider.scrollBy({
          left: event.currentTarget.name === 'next' ? scrollAmount : -scrollAmount,
          behavior: 'smooth',
        });

        window.setTimeout(() => this.updateThumbnailButtons(), 220);
      }

      shouldHandleDesktopMediaNav() {
        return window.matchMedia('(min-width: 990px)').matches && this.dataset.desktopLayout.includes('thumbnail');
      }

      navigateMedia(step) {
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        if (mediaItems.length < 2) return;

        const currentMedia = this.elements.viewer.querySelector('.is-active') || mediaItems[0];
        const currentIndex = mediaItems.indexOf(currentMedia);
        let nextIndex = currentIndex + step;

        if (nextIndex < 0) nextIndex = mediaItems.length - 1;
        if (nextIndex >= mediaItems.length) nextIndex = 0;

        this.setActiveMedia(mediaItems[nextIndex].dataset.mediaId, false);
      }

      updateThumbnailButtons() {
        if (!this.shouldHandleDesktopMediaNav() || !this.thumbnailButtons.length || !this.elements.thumbnails?.slider) return;

        const slider = this.elements.thumbnails.slider;
        const maxScrollLeft = Math.max(slider.scrollWidth - slider.clientWidth, 0);
        const currentScrollLeft = Math.round(slider.scrollLeft);
        const isAtStart = currentScrollLeft <= 2;
        const isAtEnd = currentScrollLeft >= maxScrollLeft - 2;

        this.thumbnailButtons.forEach((button) => {
          if (button.name === 'previous') {
            button.toggleAttribute('disabled', isAtStart);
          } else if (button.name === 'next') {
            button.toggleAttribute('disabled', isAtEnd);
          }
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
